/**
 * Admin · Pickup log — Tabulex-style audit of every pickup arrival.
 *
 * Hits `GET /api/admin/workspaces/{ws}/pickup-log` and `…/pickup-log.csv`.
 * Calm vocabulary; shows the authorized adult who did the pickup (or the
 * parent themselves), plus any note left for staff.
 */
import { useEffect, useState } from "react";
import { Download, Users } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";

const STATUS_LABEL = {
  arrived: "Arrived",
  running_late: "Running late",
  arriving: "On the way",
  cancelled: "Cancelled",
};

const STATUS_COLOR = {
  arrived: "#1A1B2E",
  running_late: "#D4A85F",
  arriving: "#1F3A2A",
  cancelled: "#9CA3AF",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminPickupLog() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    setLoading(true);
    api
      .get(
        `/admin/workspaces/${workspace.id}/pickup-log?days=${days}&limit=500`
      )
      .then((r) => alive && setRows(r.data || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id, days]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-pickup-log-no-ws">No workspace yet.</AdminEmpty>
    );

  const downloadCsv = async () => {
    try {
      const res = await api.get(
        `/admin/workspaces/${workspace.id}/pickup-log.csv?days=${days}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alouvya-pickup-log-${workspace.name.replace(/\s+/g, "-")}-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // calm quiet failure
    }
  };

  return (
    <div data-testid="admin-pickup-log-screen">
      <AdminHeader
        eyebrow="Pickup log"
        title="Every gentle handoff, on record"
      />
      <div className="mt-2 text-sm text-[#525252] max-w-2xl">
        A calm audit of who picked up which child, and when — including any
        notes the parent left for the team.
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2">
          <label className="text-[11px] tracking-[0.18em] uppercase text-[#525252]">
            Days
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="admin-pickup-log-days"
            className="bg-transparent outline-none text-sm font-medium"
          >
            {[7, 14, 30, 60, 90, 180, 365].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={downloadCsv}
          data-testid="admin-pickup-log-download-csv"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#132619] text-white text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#0b2117] transition-colors"
        >
          <Download size={13} strokeWidth={1.7} />
          Download CSV
        </button>
      </div>

      {loading ? (
        <AdminEmpty testid="admin-pickup-log-loading">A quiet moment…</AdminEmpty>
      ) : rows.length === 0 ? (
        <AdminEmpty testid="admin-pickup-log-empty">
          No pickup events in this window.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-pickup-log-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {rows.map((r) => {
            const picker = r.pickup_auth_name || r.parent_name;
            const announcedBy = r.pickup_auth_name ? r.parent_name : null;
            return (
              <li
                key={r.id}
                data-testid={`admin-pickup-log-row-${r.id}`}
                className="px-5 py-4 flex items-start gap-4"
              >
                <span
                  aria-hidden
                  className="h-9 w-9 rounded-2xl bg-[#F2EFEA] flex items-center justify-center shrink-0 mt-0.5"
                >
                  <Users size={14} strokeWidth={1.6} className="text-[#1A1B2E]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline flex-wrap gap-2">
                    <span className="font-serif text-base text-[#171717]">
                      {picker}
                    </span>
                    <span className="text-sm text-[#525252]">
                      picked up
                    </span>
                    <span className="font-serif text-base text-[#171717]">
                      {r.child_name}
                    </span>
                    {announcedBy && (
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                        · announced by {announcedBy}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#525252] mt-1 flex flex-wrap gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#F2EFEA]"
                      style={{ color: STATUS_COLOR[r.status] || "#525252" }}
                    >
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: STATUS_COLOR[r.status] || "#525252" }}
                      />
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                    {r.eta_minutes != null && (
                      <span>· {r.eta_minutes} min</span>
                    )}
                  </div>
                  {r.note && (
                    <div className="text-xs text-[#525252] mt-1 italic">
                      “{r.note}”
                    </div>
                  )}
                </div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[#525252] shrink-0">
                  {fmt(r.created_at)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
